// Duquesne University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Duquesne's Common Data Set 2025-2026 (cds-2025-2026.pdf on duq.edu,
//    linked from Planning, Budgeting and Institutional Research's Common Data
//    Set page): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table (20 applicants and 15 admits of unknown residency are in
//    the totals but have no row here); C8 test policy for Fall 2027; C21/C22
//    plans; G1/G5 2026-2027 costs.
//  - Duquesne Undergraduate Admissions: Application Deadlines and Important
//    Dates (the Physician Assistant deadline and the Nasuti College of
//    Osteopathic Medicine Early Assurance Program), Freshman Application
//    Process (deadlines, rolling admission after January 15), Admission
//    Application Requirements (health professions recommendation), Additional
//    Requirements (music audition, physical therapy hours, essays) and
//    Test-Optional Admission. The pages carry no cycle year; they are the
//    pages as posted for the application that opened August 1, 2026. None
//    publishes decision dates.
//  - College Scorecard, school id 212106 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 items are used. The CDS leaves required fees blank.
// The financial aid office's 2026-2027 cost of attendance lists the same
// items plus a one-time $328 new student fee and $100 in loan fees ($76,950).
// The undergraduate tuition rates page charges more in some programs: health
// sciences professional programs $61,152, pre-professional pharmacy $55,948
// and music $59,236.
// Quirk: the Requirements page says an essay is required of health
// professions applicants; the Additional Requirements page, which goes
// program by program, requires it only for Physician Assistant Studies and
// recommends it for four programs. The page follows the program-specific one.
// Quirk: the CDS marks the SAT or ACT as "recommended" for fall 2027, while
// the admissions pages call Duquesne test-optional for every program.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Duquesne Common Data Set 2025-2026',
        url: 'https://www.duq.edu/documents/institutionalresearch/cds-2025-2026.pdf',
        read: '2026-10-01',
    },
    dates: {
        label: 'Duquesne Admissions, Application Deadlines and Important Dates',
        url: 'https://www.duq.edu/admission-and-aid/how-to-apply/undergraduate/application-options-important-dates.php',
        read: '2026-10-01',
    },
    process: {
        label: 'Duquesne Admissions, Freshman Application Process',
        url: 'https://www.duq.edu/admission-and-aid/how-to-apply/undergraduate/freshman-application-process.php',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Duquesne Admissions, Admission Application Requirements',
        url: 'https://www.duq.edu/admission-and-aid/how-to-apply/undergraduate/requirements.php',
        read: '2026-10-01',
    },
    additional: {
        label: 'Duquesne Admissions, Additional Requirements',
        url: 'https://www.duq.edu/admission-and-aid/how-to-apply/undergraduate/additional-requirements.php',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'Duquesne Admissions, Test-Optional Admission',
        url: 'https://www.duq.edu/admission-and-aid/how-to-apply/undergraduate/test-optional-admission.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?212106-Duquesne-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'duquesne-university',
    kbId: 'duquesne_university',
    name: 'Duquesne University',
    shortName: 'Duquesne',
    location: 'Pittsburgh, PA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Duquesne athletics brand guidelines); decoration only.
    branding: {
        primary: '#041E42',
        secondary: '#BA0C2F',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/duquesne_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 13270,
        admitted: 11464,
        enrolled: 1404,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 7381, admitted: 6665 },
            { group: 'Out-of-state', applied: 4752, admitted: 4280 },
            { group: 'International', applied: 1117, admitted: 504 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Duquesne\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Duquesne has no early decision plan. Its early action plan is not binding and not restrictive; the Common Data Set lists a November 15 closing date and a December 15 notification date, and does not report how many applied or were admitted early.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Duquesne with an intended program. Some programs ask for more:',
        rows: [
            { school: 'Music', requirement: 'A performance audition is required for every applicant to the Mary Pappert School of Music.', source: 'additional' },
            { school: 'Physician Assistant Studies', requirement: 'Apply by November 15, choosing Early Action; the review committee starts reading after that deadline.', source: 'dates' },
            { school: 'Physician Assistant Studies essay', requirement: 'A personal essay is required: about yourself, why you want to become a physician assistant, and why Duquesne. The Common App essay does not count for this program.', source: 'additional' },
            { school: 'Health professions', requirement: 'One recommendation from a teacher or counselor is required. For other programs a recommendation is optional.', source: 'requirements' },
            { school: 'Athletic Training, Occupational Therapy, Physical Therapy, Speech-Language Pathology', requirement: 'A personal essay is recommended but not required.', source: 'additional' },
            { school: 'Physical Therapy', requirement: 'Documentation of at least 40 hours of volunteer, paid or shadowing experience in a physical therapy setting, due by August before your first year.', source: 'additional' },
            { school: 'Early Assurance Program, Nasuti College of Osteopathic Medicine', requirement: 'Open to U.S. citizens and permanent residents admitted to the Pre-Medical and Health Professions Program and an eligible major. You need at least a 3.7 unweighted GPA and a 27 ACT or 1280 SAT from one test date. Admitted seniors apply between November 1 and January 5.', source: 'dates' },
        ],
        source: 'process',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: null, binding: false },
            { plan: 'Rolling admission', deadline: 'After January 15, as space allows', decision: null, binding: false },
        ],
        note: 'You can apply with Duquesne\'s Future Focused Application, the Common App or the Coalition Application. University scholarship applications are due January 15. After January 15, admission and financial aid notices go out on a rolling basis as space allows. The admissions pages do not publish decision dates.',
        source: 'process',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Duquesne is test-optional and test-flexible for every major: you may apply without SAT, ACT or CLT scores. If you send them, they are considered, along with other scores such as AP, IB and CLEP, and Duquesne encourages you to submit scores you have. Applicants admitted without scores generally have at least a 3.0 GPA.',
        source: 'testOptional',
        cdsLine: 'The Common Data Set marks the SAT or ACT as recommended, not required, for fall 2027 applicants, with scores due by August 28.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 53238 },
            { item: 'Food and housing (on campus)', amount: 17796 },
            { item: 'Books and supplies', amount: 1440 },
            { item: 'Transportation', amount: 1018 },
            { item: 'Other expenses', amount: 3030 },
        ],
        total: 76522,
        note: 'For a full-time undergraduate living on campus. The Common Data Set lists no separate required fees.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 37704,
            byIncome: [
                { income: '$0 to $30,000', amount: 31516 },
                { income: '$30,001 to $48,000', amount: 31567 },
                { income: '$48,001 to $75,000', amount: 31675 },
                { income: '$75,001 to $110,000', amount: 33134 },
                { income: 'Over $110,000', amount: 41310 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Duquesne\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
